import React, { useCallback, useLayoutEffect, useRef } from 'react'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui'
import { AuthService } from '../../lib/api'
import {
	APP_BASENAME,
	rememberReturnTo,
	resolveReturnToForLogin,
	toRouterPath,
} from '../../lib/utils/authRedirect'
import { useAuth } from '../../hooks'

const AUTH_ERROR_MESSAGES: Record<string, string> = {
	organization_exists:
		'This organization already exists. Please ask your administrator to invite you.',
	organization_invite_pending:
		'You have a pending organization invitation. Please check your email inbox and accept the invite before signing in.',
	organization_invite_expired:
		'Your organization invite has expired. Please ask the administrator to send a new invitation.',
}

const getAuthErrorMessage = (errorDescription: string): string =>
	AUTH_ERROR_MESSAGES[errorDescription] || errorDescription

const SquareLoader: React.FC<{
	count?: number
	size?: number
	gap?: number
	duration?: number
}> = ({ count = 5, size = 16, gap = 8, duration = 1.2 }) => {
	return (
		<div
			className="flex items-center"
			style={{ gap }}
			role="status"
			aria-label="Loading…"
		>
			{Array.from({ length: count }).map((_, i) => (
				<div
					key={i}
					className="relative rounded border border-accent/40 bg-transparent overflow-hidden"
					style={{ width: size, height: size }}
				>
					<div
						className="absolute inset-0 bg-accent animate-square-fill"
						style={{
							animationDuration: `${duration}s`,
							animationDelay: `${(i * duration) / count}s`,
						}}
					/>
				</div>
			))}

			<style>
				{`
          .animate-square-fill {
            transform-origin: left center;
            transform: scaleX(0);
            animation-name: square-fill;
            animation-timing-function: cubic-bezier(.4,0,.2,1);
            animation-iteration-count: infinite;
            will-change: transform;
          }
          @keyframes square-fill {
            0%   { transform: scaleX(0);   }
            40%  { transform: scaleX(1);   }
            60%  { transform: scaleX(1);   }
            100% { transform: scaleX(0);   }
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-square-fill {
              animation: none;
              transform: scaleX(1);
            }
          }
        `}
			</style>
		</div>
	)
}

const AuthLogin: React.FC = () => {
	const { currentUser } = useAuth()
	const called = useRef(false)
	const location = useLocation()
	const navigate = useNavigate()

	const params = new URLSearchParams(location.search)
	const authError = params.get('error')
	const authErrorDescription = params.get('error_description')
	const hasAuthCallbackError = Boolean(authError && authErrorDescription)
	const returnTo =
		(location.state as { returnTo?: string } | null)?.returnTo ??
		params.get('returnTo') ??
		`${window.location.origin}${APP_BASENAME}/home`

	const handleRedirectToLogin = () => {
		sessionStorage.clear()
		navigate('/home', { replace: true })
	}

	const handleSupport = () => {
		window.open('mailto:support@solarion.ai', '_blank')
	}

	const startLogin = useCallback(() => {
		rememberReturnTo(returnTo)
		const resolvedReturnTo = resolveReturnToForLogin(returnTo)
		AuthService.login({ returnTo: resolvedReturnTo })
	}, [returnTo])

	useLayoutEffect(() => {
		if (currentUser?.id) {
			navigate(toRouterPath(returnTo), { replace: true })
			return
		}

		if (hasAuthCallbackError) return

		if (called.current) return
		called.current = true

		startLogin()
	}, [currentUser?.id, hasAuthCallbackError, navigate, returnTo, startLogin])

	if (hasAuthCallbackError) {
		return (
			<div className="fixed inset-0 h-screen w-screen bg-gray-100 dark:bg-background-dark flex items-center justify-center p-4">
				<div className="bg-white dark:bg-background-dark rounded-lg shadow-lg p-8 max-w-sm w-full text-center">
					<div className="mb-6 flex justify-center">
						<ErrorOutlineIcon className="text-[60px] text-accent" />
					</div>

					<h1 className="text-2xl font-semibold text-text-main dark:text-text-main-dark mb-4">
						Sign in failed
					</h1>

					<p className="text-text-secondary dark:text-text-table-dark mb-6">
						{getAuthErrorMessage(authErrorDescription!)}
					</p>

					<p className="text-text-secondary dark:text-text-table-dark mb-6">
						If you need help, reach out to{' '}
						<span
							className="font-medium text-accent hover:text-accent-hover cursor-pointer"
							onClick={handleSupport}
						>
							support@solarion.ai
						</span>
						.
					</p>

					<Button
						className="w-40 mx-auto"
						variant="secondary"
						label="Back to Login"
						onClick={handleRedirectToLogin}
					/>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen flex flex-col items-center justify-center p-6">
			<SquareLoader />
		</div>
	)
}

export default AuthLogin
